<template>
  <div class="payment-method-form">
    <h4 class="mb-3">Payment Method</h4>
    
    <!-- Information Banner -->
    <div class="alert alert-info mb-4">
      <i class="mdi mdi-information-outline me-2"></i>
      <strong>No charge today:</strong> Adding your payment method now allows you to easily purchase premium features later. 
      You will only be charged when you choose to upgrade to premium ($20/year).
    </div>

    <!-- Card Element Container -->
    <div class="card-element-container mb-3">
      <label class="form-label">Card Information</label>
      <div 
        id="card-element" 
        class="form-control"
        :class="{ 'is-invalid': cardError }"
        style="min-height: 40px; padding: 10px;"
      >
        <!-- Stripe Elements will mount here -->
      </div>
      <div v-if="cardError" class="invalid-feedback d-block">
        {{ cardError }}
      </div>
    </div>

    <!-- Skip Option -->
    <div class="form-check mb-3">
      <input 
        class="form-check-input" 
        type="checkbox" 
        id="skipPayment" 
        v-model="skipPaymentMethod"
        @change="handleSkipChange"
      />
      <label class="form-check-label text-muted" for="skipPayment">
        Skip adding payment method (you can add it later)
      </label>
    </div>

    <!-- Loading State -->
    <div v-if="isProcessing" class="text-center">
      <div class="spinner-border spinner-border-sm me-2" role="status"></div>
      Processing payment method...
    </div>
  </div>
</template>

<script>
import { getStripe } from '../utils/stripe';

export default {
  name: 'PaymentMethodForm',
  data() {
    return {
      stripe: null,
      elements: null,
      cardElement: null,
      cardError: null,
      isProcessing: false,
      skipPaymentMethod: false,
    };
  },
  async mounted() {
    await this.initializeStripe();
  },
  beforeUnmount() {
    if (this.cardElement) {
      this.cardElement.destroy();
    }
  },
  methods: {
    async initializeStripe() {
      try {
        this.stripe = await getStripe();
        
        if (!this.stripe) {
          console.error('Stripe failed to load');
          return;
        }

        // Create elements instance
        this.elements = this.stripe.elements({
          appearance: {
            theme: 'stripe',
            variables: {
              colorPrimary: '#0570de',
              colorBackground: '#ffffff',
              colorText: '#30313d',
              colorDanger: '#df1b41',
              fontFamily: 'Ideal Sans, system-ui, sans-serif',
              spacingUnit: '2px',
              borderRadius: '4px',
            }
          }
        });

        // Create card element
        this.cardElement = this.elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
            invalid: {
              color: '#9e2146',
            },
          },
        });

        // Mount the card element
        this.cardElement.mount('#card-element');

        // Handle real-time validation errors from the card Element
        this.cardElement.on('change', (event) => {
          this.cardError = event.error ? event.error.message : null;
          this.$emit('card-change', { 
            complete: event.complete, 
            error: event.error 
          });
        });

      } catch (error) {
        console.error('Error initializing Stripe:', error);
        this.cardError = 'Failed to load payment form. Please refresh and try again.';
      }
    },

    handleSkipChange() {
      this.$emit('skip-change', this.skipPaymentMethod);
      if (this.skipPaymentMethod) {
        this.cardError = null;
      }
    },

    async createPaymentMethod(customerInfo) {
      if (this.skipPaymentMethod) {
        return { success: true, paymentMethod: null };
      }

      if (!this.stripe || !this.cardElement) {
        return { 
          success: false, 
          error: 'Payment system not initialized' 
        };
      }

      this.isProcessing = true;
      this.cardError = null;

      try {
        const { error, paymentMethod } = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.cardElement,
          billing_details: {
            name: `${customerInfo.firstName} ${customerInfo.lastName}`,
            email: customerInfo.email,
          },
        });

        if (error) {
          this.cardError = error.message;
          return { success: false, error: error.message };
        }

        return { success: true, paymentMethod };

      } catch (error) {
        this.cardError = 'An unexpected error occurred';
        return { success: false, error: 'An unexpected error occurred' };
      } finally {
        this.isProcessing = false;
      }
    },

    // Method to validate if payment method is ready (for parent component)
    isValid() {
      return this.skipPaymentMethod || (!this.cardError && this.cardElement);
    },

    // Method to reset the form
    reset() {
      this.cardError = null;
      this.isProcessing = false;
      this.skipPaymentMethod = false;
      if (this.cardElement) {
        this.cardElement.clear();
      }
    }
  },
};
</script>

<style scoped>
.payment-method-form {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.card-element-container {
  position: relative;
}

#card-element {
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

#card-element:focus-within {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

#card-element.is-invalid {
  border-color: #dc3545;
}

.alert-info {
  border-left: 4px solid #0570de;
  background-color: #e7f3ff;
  border-color: #b8daff;
}

.form-check-label {
  font-size: 0.9rem;
}
</style>
